import { ConflictType, type TimesheetConflictModel } from '@reducers/orm/timesheet-conflict/timesheet-conflict.model';

import type { TimesheetCategory } from '../categorization/timesheet-category';
import type {
  PriorityAttentionReason,
  PriorityConflictRef,
  PriorityEntry,
  PriorityShift,
  PriorityTardiness,
  PriorityTimesheet,
  PriorityTimesheetResponse,
  TardinessBoundary,
} from './timesheet-priority.models';

export const PRIORITY_CATEGORY_TO_TIMESHEET_CATEGORY = {
  requiresAttention: 'REQUIRES_ATTENTION',
  inProgress: 'IN_PROGRESS',
  startingSoon: 'STARTING_SOON',
  readyForApproval: 'READY_FOR_APPROVAL',
  approved: 'APPROVED',
  declined: 'DECLINED',
} as const satisfies Record<string, TimesheetCategory>;

export type PriorityCategory = keyof typeof PRIORITY_CATEGORY_TO_TIMESHEET_CATEGORY;

function isPriorityCategory(value: string): value is PriorityCategory {
  return Object.hasOwn(PRIORITY_CATEGORY_TO_TIMESHEET_CATEGORY, value);
}

function isConflictType(value: unknown): value is ConflictType {
  return value === ConflictType.TIMESHEET || value === ConflictType.ABSENCE;
}

function isConflictRef(ref: unknown): ref is PriorityConflictRef {
  return (
    typeof ref === 'object' &&
    ref !== null &&
    'entityType' in ref &&
    isConflictType(ref.entityType) &&
    'entityId' in ref &&
    (typeof ref.entityId === 'string' || typeof ref.entityId === 'number')
  );
}

function toConflicts(reasons: PriorityAttentionReason[] | undefined): TimesheetConflictModel[] {
  const conflictReason = reasons?.find((reason) => reason.type === 'conflict');

  if (!conflictReason?.refs) {
    return [];
  }

  return conflictReason.refs.filter(isConflictRef).map((ref) => ({ type: ref.entityType, id: String(ref.entityId) }));
}

function isTardinessBoundary(value: unknown): value is TardinessBoundary {
  return value === 'start' || value === 'end';
}

function toTardinessRef(ref: unknown): PriorityTardiness | null {
  if (typeof ref !== 'object' || ref === null) {
    return null;
  }

  const boundary = 'boundary' in ref ? ref.boundary : null;
  const value = 'value' in ref ? ref.value : null;

  if (!isTardinessBoundary(boundary) || typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }

  const rosterId = 'rosterId' in ref ? ref.rosterId : null;

  return {
    rosterId: typeof rosterId === 'string' || typeof rosterId === 'number' ? String(rosterId) : null,
    boundary,
    minutes: value,
    isActivelyClocked: 'isActivelyClocked' in ref && ref.isActivelyClocked === true,
  };
}

function toTardiness(reasons: PriorityAttentionReason[] | undefined): PriorityTardiness[] {
  const tardinessReason = reasons?.find((reason) => reason.type === 'tardiness');

  if (!tardinessReason?.refs) {
    return [];
  }

  return tardinessReason.refs
    .map(toTardinessRef)
    .filter((tardiness): tardiness is PriorityTardiness => tardiness !== null);
}

function hasZeroDurationReason(reasons: PriorityAttentionReason[] | undefined): boolean {
  return reasons?.some((reason) => reason.type === 'zeroDuration') ?? false;
}

function toCategory(category: string): TimesheetCategory | null {
  return isPriorityCategory(category) ? PRIORITY_CATEGORY_TO_TIMESHEET_CATEGORY[category] : null;
}

export function isPriorityShift(entry: PriorityEntry): entry is PriorityShift {
  return 'occurrenceId' in entry;
}

/**
 * `timesheetId: null` marks a scheduled shift nobody has clocked in for. A shift row without its
 * ids cannot be keyed or attributed to an employee, so it is dropped (`null`).
 */
export function normalizePriorityEntry(response: PriorityTimesheetResponse): PriorityEntry | null {
  return response.timesheetId == null ? normalizePriorityShift(response) : normalizePriorityTimesheet(response);
}

/** The backend adds categories and reason types without a web release. */
export function normalizePriorityTimesheet(response: PriorityTimesheetResponse): PriorityTimesheet {
  return {
    timesheetId: String(response.timesheetId),
    category: toCategory(response.category),
    conflicts: toConflicts(response.reasons),
    hasZeroDuration: hasZeroDurationReason(response.reasons),
    tardiness: toTardiness(response.reasons),
  };
}

function normalizePriorityShift(response: PriorityTimesheetResponse): PriorityShift | null {
  const { occurrenceId, employeeId } = response;

  if (!occurrenceId || employeeId == null || employeeId === '') {
    return null;
  }

  return {
    occurrenceId,
    employeeId: String(employeeId),
    departmentId: response.departmentId == null ? undefined : String(response.departmentId),
    date: response.date ?? '',
    category: toCategory(response.category),
    isNoShow: response.reasons?.some((reason) => reason.type === 'noShow') ?? false,
  };
}
